import { useParams, Link } from 'react-router-dom'
import { useState, useEffect, useMemo, useTransition } from 'react'
import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Navbar } from '@/components/navbar'
import { NewsCard, NewsCardSkeleton } from '@/components/news-card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { newsApi } from '@/services/api'
import { ArrowLeft, Loader2, Hash } from 'lucide-react'
import { motion } from 'framer-motion'
import { getViewMode } from '@/lib/utils'

export function TagsPage() {
  const { slug } = useParams()
  const [viewMode, setViewMode] = useState(getViewMode())
  const [isPending, startTransition] = useTransition()

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '200px',
  })

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['tag', slug],
    queryFn: ({ pageParam = 1 }) => newsApi.getByTag(slug, pageParam),
    getNextPageParam: (lastPage) => {
      if (!lastPage.data.next) return undefined
      
      // Extract page number from URL
      try {
        const url = new URL(lastPage.data.next)
        const page = url.searchParams.get('page')
        return page ? parseInt(page, 10) : undefined
      } catch {
        return undefined
      }
    },
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  })

  const articles = useMemo(() => {
    return data?.pages.flatMap(page => page.data.results) || []
  }, [data?.pages])
  
  const tagInfo = articles[0]?.tags?.find(t => t.slug === slug)

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      startTransition(() => {
        fetchNextPage()
      })
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  // Related tags (mock data - you can fetch from API)
  const relatedTags = [
    'technology', 'business', 'politics', 'science', 'health', 'sports'
  ].filter(t => t !== slug)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar onViewModeChange={setViewMode} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="h-8 w-24 bg-gray-200 rounded animate-shimmer mb-6" />
            <div className="bg-white rounded-xl p-8 mb-8">
              <div className="space-y-3">
                <div className="h-10 bg-gray-200 rounded animate-shimmer w-64" />
                <div className="h-4 bg-gray-200 rounded animate-shimmer w-full" />
              </div>
            </div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <NewsCardSkeleton key={i} variant={viewMode} />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar onViewModeChange={setViewMode} />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="bg-red-50 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
            <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Tag Not Found</h1>
          <p className="text-gray-600 mb-6">The tag you're looking for doesn't exist.</p>
          <Link to="/">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const tagName = tagInfo?.name || slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onViewModeChange={setViewMode} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back button */}
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-primary mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">Back to Feed</span>
          </Link>

          {/* Tag header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-8 shadow-sm mb-8"
          >
            <div className="flex items-start gap-4">
              <div data-logo-box className="bg-primary rounded-full p-4 transition-all duration-300">
                <Hash className="w-8 h-8 text-white" />
              </div>
              
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {tagName}
                </h1>
                <p className="text-gray-600 mb-4">
                  Explore articles tagged with <strong>{tagName}</strong>
                </p>
                
                {/* Related tags */}
                {relatedTags.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Related tags:</p>
                    <div className="flex flex-wrap gap-2">
                      {relatedTags.slice(0, 5).map((tag) => (
                        <Link key={tag} to={`/tags/${tag}`}>
                          <Badge variant="secondary" className="hover:bg-gray-200 transition-colors">
                            #{tag}
                          </Badge>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="text-center">
                <div className="text-3xl font-bold text-primary">{articles.length}+</div>
                <div className="text-sm text-gray-500">Articles</div>
              </div>
            </div>
          </motion.div>

          {/* Articles with this tag */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Articles tagged #{tagName}</h2>
          </div>

          {articles.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500">No articles found with this tag.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {articles.map((article, index) => (
                <NewsCard
                  key={article.alias || article.id}
                  article={article}
                  variant={viewMode}
                  index={index}
                />
              ))}
              
              {/* Loading next page skeletons */}
              {(isFetchingNextPage || isPending) && (
                <>
                  {[...Array(3)].map((_, i) => (
                    <NewsCardSkeleton key={`skeleton-${i}`} variant={viewMode} />
                  ))}
                </>
              )}
            </div>
          )}

          {/* Load more trigger */}
          <div ref={loadMoreRef} className="py-4">
            {!hasNextPage && articles.length > 0 && (
              <p className="text-sm text-gray-500 text-center">You've reached the end</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
